import { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useAlertContext, useUserContext } from "../contexts";
import { BuildUrl, formatCentreName } from "../helpers";
import { AlertType, Holiday, HolidayClinic } from "../models";
import {
	Header,
	HolidaysTable,
	UpdateHolidayModal
} from "../components/HolidaysAdmin";
import { useMsal } from "@azure/msal-react";
import { apiAccessRequest } from "../constants";
import { addYears } from "date-fns/addYears";
import { parse } from "date-fns/parse";
import { format } from "date-fns/format";
import { isBefore } from "date-fns/isBefore";
import { isValid } from "date-fns/isValid";
import { startOfToday } from "date-fns/startOfToday";

export const HolidayAdmin = () => {
	const { showAlert } = useAlertContext();
	const { account } = useUserContext();
	const { instance } = useMsal();

	const [holidays, setHolidays] = useState<Holiday[]>([]);
	const [displayCreateModal, setDisplayCreateModal] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isError, setIsError] = useState(false);

	const getHolidays = useCallback(async () => {
		try {
			setIsLoading(true);
			setIsError(false);
			const requestUrl = BuildUrl(`Holiday/all`);
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				...(tokenResponse && {
					Authorization: `Bearer ${tokenResponse.accessToken}`
				}),
				...{ "Content-Type": "application/json" }
			};
			const response = await fetch(requestUrl, {
				method: "GET",
				headers
			});

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}

			const contentLength = response.headers.get("Content-Length");
			if (response.status !== 204 && contentLength !== "0") {
				const result = await response.json();
				const today = startOfToday();
				const holidays = result
					.map((holiday: Holiday) => {
						const holidayDate = parse(holiday.date, "yyyy-MM-dd", new Date());
						let displayDate = holidayDate;

						if (holiday.recurring && isValid(holidayDate)) {
							const thisYearsDate = parse(
								`${format(holidayDate, "dd/MM")}/${format(today, "yyyy")}`,
								"dd/MM/yyyy",
								new Date()
							);

							if (isValid(thisYearsDate)) {
								displayDate = isBefore(thisYearsDate, today)
									? addYears(thisYearsDate, 1)
									: thisYearsDate;
							}
						}
						const displayDateValue = format(displayDate, "yyyy-MM-dd");

						return {
							holiday: {
								...holiday,
								date: format(displayDate, "dd/MM/yyyy"),
								displayDate: displayDateValue,
								clinics: holiday.clinics.map((clinic: HolidayClinic) => ({
									...clinic,
									clinicName: formatCentreName(clinic.clinicName)
								}))
							},
							displayDate: displayDateValue
						};
					})
					.sort(
						(first: { displayDate: string }, second: { displayDate: string }) =>
							second.displayDate.localeCompare(first.displayDate)
					)
					.map(({ holiday }: { holiday: Holiday }) => holiday);
				setHolidays(holidays);
			}
		} catch (error) {
			console.info(error);
			showAlert({
				message: "Holidays could not be retrieved",
				alertType: AlertType.error
			});
			setIsError(true);
		} finally {
			setIsLoading(false);
		}
	}, [account, instance, showAlert]);

	useEffect(() => {
		getHolidays();
	}, [getHolidays]);

	const closeModal = (isUpdate: boolean) => {
		if (isUpdate) {
			getHolidays();
		}
	};

	if (isLoading) {
		return (
			<Box className="icon-loading">
				<CircularProgress size={70} />
			</Box>
		);
	}

	if (isError) return <></>;

	return (
		<>
			<Header onCreateClick={() => setDisplayCreateModal(true)} />
			<HolidaysTable holidays={holidays} onModalClose={closeModal} />

			<UpdateHolidayModal
				open={displayCreateModal}
				mode="create"
				onClose={(isUpdate) => {
					setDisplayCreateModal(false);
					closeModal(isUpdate);
				}}
			/>
		</>
	);
};
